import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateMedicationCreateBulaParams } from './dtos/createBula.dto';
import type { CreateMedicationCreateMessageParams } from './dtos/createMessage.dto';
import type { CreateMedicationCreateTopicParams } from './dtos/createTopic.dto';
import type { GetAllMedicationCreatesAvailableParams } from './dtos/getAllAvailable.dto';
import type { GetAllMedicationCreatesByCollaboratorParams } from './dtos/getAllByCollaborator.dto';
import type { ImportMedicationCreateTopicsParams } from './dtos/importTopics.dto';
import type { RemoveMedicationCreateTopicParams } from './dtos/remove.dto';
import type { RemoveAllMedicationCreateTopicsParams } from './dtos/removeAll.dto';
import type { RemoveMedicationCreateBulaParams } from './dtos/removeBula.dto';
import type { ReorderMedicationCreateTopicsParams } from './dtos/reorder.dto';
import type { ReorderMedicationCreateBulasParams } from './dtos/reorderBulas.dto';
import type { UpdateMedicationCreateTopicParams } from './dtos/update.dto';
import type { UpdateMedicationCreateBulaParams } from './dtos/updateBula.dto';
import type { UpdateMedicationCreateBulaFileParams } from './dtos/updateBulaFile.dto';
import type { UpdateMedicationCreateMessageParams } from './dtos/updateMessage.dto';
import type { MedicationCreatesRepository } from './repository';
import type { CreateMessageBody } from './types/createMessage.type';
import type { CreateTopicBody } from './types/createTopic.type';
import type {
  GetAllMedicationCreatesAvailableOutput,
  GetAllMedicationCreatesAvailableResponse,
} from './types/getAllAvailable.type';
import type {
  GetAllMedicationCreatesByCollaboratorOutput,
  GetAllMedicationCreatesByCollaboratorResponse,
} from './types/getAllByCollaborator.type';
import type {
  GetMedicationCreateByIdDataResponse,
  GetMedicationCreateByIdOutput,
} from './types/getById.type';
import type {
  ImportTopicsOutput,
  ImportTopicsResponse,
} from './types/importTopics.type';
import type { ReorderBulasBody } from './types/reorderBulas.type';
import type { ReorderTopicsBody } from './types/reorderTopics.type';
import type { UpdateBulaBody } from './types/updateBula.type';
import type { UpdateMessageBody } from './types/updateMessage.type';
import type { UpdateTopicBody } from './types/updateTopic.type';

const UPLOAD_TIMEOUT = 5 * 60 * 1000;

class MedicationCreatesService implements MedicationCreatesRepository {
  public async getAllByCollaborator(
    params?: GetAllMedicationCreatesByCollaboratorParams,
  ): Promise<GetAllMedicationCreatesByCollaboratorOutput> {
    const { data } = await backendHttpClient.get<
      GetAllMedicationCreatesByCollaboratorResponse,
      GetAllMedicationCreatesByCollaboratorParams
    >({
      path: 'medication-creates/collaborator',
      params,
    });

    const output: GetAllMedicationCreatesByCollaboratorOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        startedAt: item.started_at ?? undefined,
        expiredAt: item.expired_at ?? undefined,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages ?? 1,
      },
    };
    return output;
  }

  public async getAllAvailable(
    params?: GetAllMedicationCreatesAvailableParams,
  ): Promise<GetAllMedicationCreatesAvailableOutput> {
    const { data } = await backendHttpClient.get<
      GetAllMedicationCreatesAvailableResponse,
      GetAllMedicationCreatesAvailableParams
    >({
      path: 'medication-creates/available',
      params,
    });

    const output: GetAllMedicationCreatesAvailableOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        createdAt: item.created_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages ?? 1,
      },
    };
    return output;
  }

  public async getById(id: string): Promise<GetMedicationCreateByIdOutput> {
    const { data: medication } =
      await backendHttpClient.get<GetMedicationCreateByIdDataResponse>({
        path: `medication-creates/${id}`,
      });
    const output: GetMedicationCreateByIdOutput = {
      medication: {
        amount: medication.amount,
        bulas: medication.bulas.map(item => ({
          documentDate: item.document_date,
          fileUrl: item.file_url,
          id: item.id,
          label: item.label,
          originUrl: item.origin_url,
          position: item.position,
          referenceNumber: item.reference_number,
          source: item.source,
        })),
        collaborator: medication.collaborator,
        expiredAt: medication.expired_at,
        id: medication.id,
        priority: medication.priority,
        status: medication.status,
        title: medication.title,
        topics: medication.topics.map(item => ({
          content: item.content,
          id: item.id,
          title: item.title ?? '',
        })),
        messages: medication.messages.map(item => ({
          createdAt: item.created_at,
          id: item.id,
          medicationId: item.medication_create_id,
          message: item.message,
          publishedAt: item.published_at,
          type: item.type,
        })),
      },
    };
    return output;
  }

  public async assignCollaborator(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `medication-creates/${id}/collaborator`,
    });
  }

  public async unassignCollaborator(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-creates/${id}/collaborator`,
    });
  }

  public async removeAllTopics(
    params: RemoveAllMedicationCreateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-creates/${params.medicationCreateId}/topics`,
    });
  }

  public async removeTopic(
    params: RemoveMedicationCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-creates/${params.medicationCreateId}/topics/${params.id}`,
    });
  }

  public async updateTopic(
    params: UpdateMedicationCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateTopicBody>({
      path: `medication-creates/${params.medicationCreateId}/topics/${params.id}`,
      body: {
        content: params.content,
        title: params.title,
      },
    });
  }

  public async createMessage(
    params: CreateMedicationCreateMessageParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateMessageBody>({
      path: `medication-creates/${params.medicationCreateId}/messages`,
      body: {
        message: params.message,
      },
    });
  }

  public async updateMessage(
    params: UpdateMedicationCreateMessageParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateMessageBody>({
      path: `medication-creates/${params.medicationCreateId}/messages/${params.medicationCreateMessageId}`,
      body: {
        message: params.message,
      },
    });
  }

  public async createTopic(
    params: CreateMedicationCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateTopicBody>({
      path: `medication-creates/${params.medicationCreateId}/topics`,
      body: {
        content: params.content,
        position: params.position,
        title: params.title,
      },
    });
  }

  public async importTopics(
    params: ImportMedicationCreateTopicsParams,
  ): Promise<ImportTopicsOutput> {
    const formData = new FormData();

    formData.append('file', params.file);

    const { data } = await backendHttpClient.post<
      ImportTopicsResponse,
      FormData
    >({
      path: `medication-creates/${params.medicationCreateId}/topics/import`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });

    return {
      bulasAdded: data.bulasAdded,
      bulasReplaced: data.bulasReplaced,
      topicsCreated: data.topicsCreated,
      warnings: data.warnings,
    };
  }

  public async reorderTopics(
    params: ReorderMedicationCreateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderTopicsBody>({
      path: `medication-creates/${params.medicationCreateId}/topics`,
      body: {
        ids: params.ids,
      },
    });
  }

  public async markReview(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `medication-creates/${id}/review`,
    });
  }

  public async createBula(
    params: CreateMedicationCreateBulaParams,
  ): Promise<void> {
    const formData = new FormData();

    // campos vazios ficam de fora; o arquivo vai por último
    const fields: Record<string, string | number | undefined> = {
      document_date: params.documentDate,
      label: params.label,
      origin_url: params.originUrl,
      reference_number: params.referenceNumber,
      source: params.source,
    };
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined && value !== '') {
        formData.append(key, String(value));
      }
    }
    formData.append('file', params.file);

    await backendHttpClient.post<void, FormData>({
      path: `medication-creates/${params.medicationCreateId}/bulas`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });
  }

  public async updateBula(
    params: UpdateMedicationCreateBulaParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateBulaBody>({
      path: `medication-creates/${params.medicationCreateId}/bulas/${params.id}`,
      body: {
        document_date: params.documentDate || null,
        label: params.label,
        origin_url: params.originUrl || null,
        reference_number: params.referenceNumber ?? null,
        source: params.source ?? null,
      },
    });
  }

  public async updateBulaFile(
    params: UpdateMedicationCreateBulaFileParams,
  ): Promise<void> {
    const formData = new FormData();

    formData.append('file', params.file);

    await backendHttpClient.put<void, FormData>({
      path: `medication-creates/${params.medicationCreateId}/bulas/${params.id}/file`,
      body: formData,
      timeout: UPLOAD_TIMEOUT,
    });
  }

  public async removeBula(
    params: RemoveMedicationCreateBulaParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `medication-creates/${params.medicationCreateId}/bulas/${params.id}`,
    });
  }

  public async reorderBulas(
    params: ReorderMedicationCreateBulasParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderBulasBody>({
      path: `medication-creates/${params.medicationCreateId}/bulas`,
      body: {
        ids: params.ids,
      },
    });
  }
}

export const medicationCreatesService = new MedicationCreatesService();
